// src/pages/Platform/Chatbot/ChatbotBuilder/KnowledgeBase/OpenAI/OpenAIFiles.js

import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, Alert, CircularProgress, ListItemIcon, ListItemText, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { FileText, Trash2, Upload, CheckCircle } from 'react-feather';
import axios from 'axios';
import logger from '../../../../../../utils/logger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import KnowledgeBaseOverview from '../KnowledgeBaseOverview';

// Universal Components
import UniversalKnowledgeBasePanel from '../components/UniversalKnowledgeBasePanel';
import UniversalList, { UniversalListItem } from '../components/UniversalList';
import { StatusChip, ItemTitle } from '../components/CommonStyles';

// --- Styled Components ---

const UploadButton = styled(Button)(({ theme }) => ({
    width: '100%',
    backgroundColor: '#000000',
    color: '#ffffff',
    padding: '12px',
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 600,
    marginTop: '24px',
    '&:hover': {
        backgroundColor: '#333333',
    },
    '&:disabled': {
        backgroundColor: '#e2e8f0',
        color: '#94a3b8',
    },
}));

function OpenAIFiles({ selectedChatbotId }) {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({ hasMore: false, offset: 0, total: 0 });

    const fetchFiles = useCallback(async (reset = true, customOffset = 0) => {
        if (!selectedChatbotId) return;

        if (reset) setLoading(true);
        setError(null);

        try {
            const offset = reset ? 0 : customOffset;
            const response = await axios.get(`/api/openai-assistant/files/${selectedChatbotId}?limit=25&offset=${offset}`);

            const newFiles = response.data.files || [];
            const pageData = response.data.pagination || {};

            if (reset) {
                setFiles(newFiles);
            } else {
                setFiles(prev => [...prev, ...newFiles]);
            }

            setPagination({
                hasMore: pageData.hasMore || false,
                offset: pageData.nextOffset || 0,
                total: pageData.total || newFiles.length
            });

        } catch (err) {
            logger.error('Error fetching OpenAI files:', err);
            setError('Failed to fetch files from OpenAI.');
        } finally {
            if (reset) setLoading(false);
        }
    }, [selectedChatbotId]);

    useEffect(() => {
        fetchFiles();
    }, [fetchFiles]);

    const handleFileUpload = async (e) => {
        const fileList = e.target.files;
        if (!fileList || fileList.length === 0) return;

        setUploading(true);
        setError(null);

        try {
            for (const file of Array.from(fileList)) {
                const formData = new FormData();
                formData.append('file', file);

                await axios.post(`/api/openai-assistant/files/${selectedChatbotId}/upload`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            }
            // Refresh list
            fetchFiles(true);
        } catch (err) {
            logger.error('Error uploading file:', err);
            setError('Failed to upload file to OpenAI.');
        } finally {
            setUploading(false);
            // Reset input
            e.target.value = '';
        }
    };

    const handleDelete = async (fileId) => {
        if (!window.confirm('Are you sure you want to delete this file from OpenAI?')) return;

        try {
            await axios.delete(`/api/openai-assistant/files/${selectedChatbotId}/${fileId}`);
            setFiles(prev => prev.filter(f => f.id !== fileId));
            setPagination(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
        } catch (err) {
            logger.error('Error deleting file:', err);
            setError('Failed to delete file.');
        }
    };

    const loadMore = () => {
        if (!pagination.hasMore) return;
        fetchFiles(false, pagination.offset);
    };

    const renderFileItem = (file) => (
        <UniversalListItem
            key={file.id}
            secondaryAction={
                <IconButton edge="end" size="small" onClick={() => handleDelete(file.id)} sx={{ color: '#ef4444' }}>
                    <Trash2 size={16} />
                </IconButton>
            }
        >
            <ListItemIcon sx={{ minWidth: '40px' }}>
                <FileText size={20} color="#3b82f6" />
            </ListItemIcon>
            <ListItemText
                primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <ItemTitle title={file.filename}>
                            {file.filename}
                        </ItemTitle>
                        <StatusChip
                            icon={<CheckCircle />}
                            label={file.status || 'Active'}
                            size="small"
                        />
                    </Box>
                }
                secondary={
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                        {file.bytes ? `${(file.bytes / 1024).toFixed(1)} KB` : 'Unknown size'} • {new Date(file.created_at * 1000).toLocaleDateString()}
                    </Typography>
                }
            />
        </UniversalListItem>
    );

    const rightPanelContent = (
        <KnowledgeBaseOverview selectedChatbotId={selectedChatbotId}>
            <input
                type="file"
                id="openai-file-upload"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileUpload}
            />
            <label htmlFor="openai-file-upload" style={{ width: '100%' }}>
                <UploadButton component="span" disabled={uploading}>
                    {uploading ? (
                        <>
                            <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} />
                            Uploading...
                        </>
                    ) : (
                        <>
                            <Upload size={18} style={{ marginRight: 8 }} />
                            Upload Files
                        </>
                    )}
                </UploadButton>
            </label>
        </KnowledgeBaseOverview>
    );

    const emptyState = (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', p: 4, color: '#94a3b8' }}>
            <FileText size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
            <Typography>No files found in OpenAI Assistant.</Typography>
        </Box>
    );

    return (
        <UniversalKnowledgeBasePanel
            title="OpenAI Files"
            subtitle="Manage files uploaded directly to your OpenAI Assistant."
            rightPanelContent={rightPanelContent}
        >
            {error && <Alert severity="error" sx={{ mb: 2, mx: 3, mt: 3 }}>{error}</Alert>}

            {loading && files.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress size={24} />
                </Box>
            ) : (
                <Box sx={{ p: 3 }}>
                    <UniversalList
                        items={files}
                        renderItem={renderFileItem}
                        emptyState={emptyState}
                    />
                    {pagination.hasMore && (
                        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                            <Button onClick={loadMore} disabled={loading}>
                                {loading ? 'Loading...' : 'Load More'}
                            </Button>
                        </Box>
                    )}
                </Box>
            )}
        </UniversalKnowledgeBasePanel>
    );
}

export default OpenAIFiles;
